import moment from "moment";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { Relationship } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { toLookupDictionary } from "@/Common/Utilities/Collections/ArrayUtility";
import { QuebecVaccinationRegistryHelper } from "@/Patient/Components/MedicalItems/Immunization/QuebecVaccinationRegistryHelper";
import { SyncRvqImmunizationPopup, SyncRvqImmunizationPopupProps } from "@/Patient/Components/MedicalItems/Immunization/SyncRvqImmunizationPopup";
import { ScreeningPlan } from "@/Patient/Components/MedicalNote/Sections/ScreeningPlan";
import { MedicalItemRiskFactorHelper } from "@/Patient/Helpers/MedicalItemRiskFactorHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { ClickableMedicalReactionItem } from "../../MedicalItems/ClickableMedicalReactionItem";
import { ActiveMedication } from "./ActiveMedication";
import { ProblemsSummary } from "./ProblemsSummary";
import { SummaryItems } from "./SummaryItems";
import { VitalSignSummary } from "./VitalSignSummary";

interface IMedicalSummarySection {
    noteModel: IPatientMedicalNoteModel;
    medicalSummaryModel: IPatientMedicalSummaryModel;
    dateOfBirth: Date;
    vitalSignPercentileUtility: VitalSignPercentileUtility;
    immunizations: MedicalItemImmunization[];
    immunizationSyncPopupProps: SyncRvqImmunizationPopupProps;
    isEditMode: boolean;
    isReadOnly: boolean;
    toggleSectionOpen: () => void;
    onMedicalItemClick: (item: MedicalItem, view: MedicalItemsTab) => void;
    addMedicalHistoryItem: () => void;
    addVitalSignItem: () => void;
    onRvqStatusClick: (immunization: MedicalItemImmunization, event: React.MouseEvent) => Promise<void>;
    isSectionOpen: boolean;
}

function MedicalSummarySection(props: IMedicalSummarySection): JSX.Element {
    const { getText } = useTranslation();
    const { noteModel, isEditMode, addVitalSignItem } = props;
    const encounter = noteModel.get("Encounter");
    const isSignedOff = encounter.IsSignedOff;

    const summaryItems = [
        {
            isSignedOff,
            isEditMode,
            title: getText("AllergiesAndIntoleranceLabel"),
            dataCy: "allergiesAndIntolerance",
            content: getMedicalReactionRenders(),
            addItem: () => onAddSummaryItem(addMedicalReactionItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("SocialHabitsLabel"),
            dataCy: "socialHabits",
            content: getRiskFactorRenders(),
            addItem: () => onAddSummaryItem(addRiskFactorItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("AlertsAndSpecialNeedsLabel"),
            dataCy: "alertsAndSpecialNeeds",
            content: getAlertAndSpecialNeedRenders(),
            addItem: () => onAddSummaryItem(addAlertItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("FamilyHistoryLabel"),
            dataCy: "familyHistory",
            content: getFamilyHistoryRenders(),
            addItem: () => onAddSummaryItem(addFamilyHistoryItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("ImmunizationsLabel"),
            dataCy: "immunizations",
            content: getImmunizationRenders(),
            addItem: () => onAddSummaryItem(addImmunizationItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("ActiveMedicationLabel"),
            dataCy: "activeMedication",
            content: getActiveMedicationRenders(),
            addItem: () => onAddSummaryItem(addActiveMedicationItem)
        },
        {
            isSignedOff,
            isEditMode,
            title: getText("VitalSignsLabel"),
            dataCy: "vitalSigns",
            content: getVitalSignRenders(),
            addItem: () => onAddSummaryItem(addVitalSignItem)
        }
    ];

    function onAddSummaryItem(callback?: () => void): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.CancelMedicalItemActionFromOutsideNote);
        callback?.();
    }

    function addScreeningPlanItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewScreeningTestItem);
    }

    function addMedicalReactionItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewMedicalReactionItem);
    }

    function addRiskFactorItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewRiskFactorItem);
    }

    function addAlertItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewAlertSpecialNeedItem);
    }

    function addFamilyHistoryItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewFamilyHistoryItem);
    }

    function addImmunizationItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem);
    }

    function addActiveMedicationItem(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewActiveMedication);
    }

    function getMedicalReactionRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsMedicalReaction").toArray();
        const problemsToDisplay: MedicalItemMedicalReaction[] = PatientMedicalSummaryHelper
            .GetMedicalItemToDisplayForMedicalNote(allItems, props.medicalSummaryModel, noteModel) as MedicalItemMedicalReaction[];

        return isArrayNullOrUndefinedOrEmpty(problemsToDisplay) ? null : (
            <ClickableMedicalReactionItem
                allergies={problemsToDisplay}
                onMedicalItemClick={(model: MedicalItemMedicalReaction, view: MedicalItemsTab) => props.onMedicalItemClick(model, view)}
            />
        );
    }

    function getVitalSignRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsVitalSign").toArray();
        let problemsToDisplay: MedicalItemVitalSign[] = PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalNote(allItems, props.medicalSummaryModel, noteModel) as MedicalItemVitalSign[];

        return isArrayNullOrUndefinedOrEmpty(problemsToDisplay) ? null : (
            <VitalSignSummary
                vSign={problemsToDisplay.sortByPropertyDesc(x => x.CommitDate)[0]}
                dateOfBirth={props.dateOfBirth}
                encounterId={encounter.Id}
                vitalSignPercentileUtility={props.vitalSignPercentileUtility}
                onMedicalItemClick={(item, view) => props.onMedicalItemClick(item, view)}
                addVitalSign={() => props.addVitalSignItem()}
            />
        );
    }

    function getRiskFactorRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsRiskFactor").toArray();
        let problemsToDisplay: MedicalItemRiskFactor[] = PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalNote(allItems, props.medicalSummaryModel, noteModel) as MedicalItemRiskFactor[];
        let result: JSX.Element[] = [];
        problemsToDisplay = ArrayHelper.SortDynamic(problemsToDisplay, "PharmaStartDate");

        result = problemsToDisplay.map((riskFactor, idx) => {
            const comma = (idx == problemsToDisplay.length - 1) ? "" : ",";

            const invalidStyle = riskFactor.DisplayAsInvalid ? "invalid-medical-item" : "";

            return (
                <div key={riskFactor.Id} className="note-items-list" onClick={() => props.onMedicalItemClick(riskFactor, MedicalItemsTab.RiskFactors)}>
                    <a><span className={invalidStyle}>{MedicalItemRiskFactorHelper.getRiskFactorLabel(riskFactor, true)}{comma}</span></a>
                </div>
            );
        });

        return isArrayNullOrUndefinedOrEmpty(problemsToDisplay) ? null : <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>{result}</div>;
    }

    function getAlertAndSpecialNeedRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsAlertAndSpecialNeed").toArray();
        const problemsToDisplay: MedicalItemAlertsAndSpecialNeeds[] = PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalNote(allItems, props.medicalSummaryModel, noteModel) as MedicalItemAlertsAndSpecialNeeds[];
        let result: JSX.Element[] = [];

        result = problemsToDisplay.map((x, idx) => {
            const comma = (idx == problemsToDisplay.length - 1) ? "" : ",";

            const invalidStyle = x.DisplayAsInvalid ? "invalid-medical-item" : "";
            return (
                <div key={x.Id} className="note-items-list" onClick={() => props.onMedicalItemClick(x, MedicalItemsTab.Alerts)}>
                    <a><span className={invalidStyle + " med-alert-lng"} title={x.Name}>{x.Name}</span><span className="med-alert-lng">{comma}</span></a>
                </div>
            );
        });

        return isArrayNullOrUndefinedOrEmpty(problemsToDisplay) ? null : <>{result}</>;
    }

    function getFamilyHistoryRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsFamilyHistory").toArray();
        const problemsToDisplay: MedicalItemFamilyHistory[] = PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalNote(allItems, props.medicalSummaryModel, noteModel) as MedicalItemFamilyHistory[];
        let result: JSX.Element[] = [];
        const otherRelationShipLookupId: string = cacheStore.FindLookupByLookupCode(Relationship.Other, Relationship.Code).LookupID;

        result = problemsToDisplay.map((x, idx) => {
            let relationShipString = cacheStore.GetLocalizedValueByLookupId(x.RelationshipLookupId);
            if (!IsNullOrWhiteSpace(relationShipString))
                relationShipString = `(${relationShipString})`;

            if (x.RelationshipLookupId == otherRelationShipLookupId)
                relationShipString = `${relationShipString} : ${x.SpecifiedRelationship}`;

            const invalidStyle = x.DisplayAsInvalid ? "invalid-medical-item" : "";

            const comma = (idx == problemsToDisplay.length - 1) ? "" : ",";

            return (
                <div key={x.Id} className="note-items-list" onClick={() => props.onMedicalItemClick(x, MedicalItemsTab.FamilyHistory)}>
                    <a><span className={invalidStyle}>{x.Problem} {relationShipString} {comma}</span></a>
                </div>
            );
        });

        return isArrayNullOrUndefinedOrEmpty(problemsToDisplay) ? null : <>{result}</>;
    }

    function getImmunizationRenders(): JSX.Element {
        const lastIndex = props.immunizations.length - 1;

        const immunizationJsxElements = PatientMedicalSummaryHelper
            .GetMedicalItemToDisplayForMedicalNote(
                props.immunizations,
                props.medicalSummaryModel,
                noteModel
            )
            .map(p => p as MedicalItemImmunization)
            .sortByProperty(i => i.CreationDate)
            .map((immunization, index) => {
                const invalidStyle = immunization.DisplayAsInvalid ? "invalid-medical-item" : "";
                const refusedStyle = immunization.IsRefused ? "red-text" : "";

                const rvqSyncUpToDate = QuebecVaccinationRegistryHelper.isQuebecRegistryVaccinationSynced(immunization);
                const rvqSyncStatus = rvqSyncUpToDate
                    ? `${getText("QuebecVaccinationRegistry_SyncStatus_Submitted")} ${getText("AtLabel")} ${QuebecVaccinationRegistryHelper.getQuebecRegistryVaccinationSyncDate(immunization)}`
                    : getText(
                        !immunization.QuebecRegistryDeletionDate && immunization.SubmittedToQuebecRegistryOn < immunization.CommitDate
                            ? "QuebecVaccinationRegistry_SyncStatus_EditedAfterSubmission"
                            : "QuebecVaccinationRegistry_SyncStatus_NotSubmitted"
                    );

                const refusedDateString = immunization.RefusedDate != null
                    ? "(" + PatientMedicalItemsHelper.GetLifeStageDateString(immunization.RefusedDate, immunization.RefusedPrecisionDate) + ")"
                    : "";

                const administrationDateString = immunization.AdministeredDate != null
                    ? "(" + PatientMedicalItemsHelper.GetLifeStageDateString(immunization.AdministeredDate, immunization.AdministeredPrecisionDate) + ")"
                    : "";

                const syncStatusSymbol = <span style={{ color: rvqSyncUpToDate ? "green" : "red", paddingRight: "2px" }}>{rvqSyncUpToDate ? "✓" : "✘"}</span>;

                const isLast = index === lastIndex;

                const displayName = QuebecVaccinationRegistryHelper.getImmunizationDisplayName(immunization);
                const showRvqStatus = cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry && immunization.IsQuebecRegistryForm;

                return (
                    <div key={immunization.Id} style={{ display: "flex", marginBottom: "3px", marginRight: isLast ? "0" : "5px" }}>
                        {immunization === props.immunizationSyncPopupProps?.immunization &&
                            <SyncRvqImmunizationPopup {...props.immunizationSyncPopupProps} />
                        }
                        <a onClick={() => props.onMedicalItemClick(immunization, MedicalItemsTab.Immunization)}>
                            <span className={`${refusedStyle} ${invalidStyle}`}>
                                {displayName} {(immunization.IsRefused ? refusedDateString : administrationDateString)}
                            </span>
                            {!showRvqStatus && !isLast && ","}
                        </a>
                        {showRvqStatus && (
                            <a onClick={event => props.onRvqStatusClick(immunization, event)} title={rvqSyncStatus} style={{ marginRight: "5px", marginLeft: "3px", color: "gray" }}>
                                ({syncStatusSymbol}{getText("RVQLabel")}){!isLast && ","}
                            </a>
                        )}
                    </div>
                );
            });

        return immunizationJsxElements.any()
            ? (
                <>
                    <div key="past_meds" style={{ display: "flex", flexWrap: "wrap" }}>
                        {immunizationJsxElements}
                    </div>
                </>
            )
            : null;
    }

    function getActiveMedicationRenders(): JSX.Element {
        const allItems = props.medicalSummaryModel.get("MedicalItemsMedicationAndPrescription");
        const medModel = allItems.map(x => x.Model).toArray();
        const medicationToDisplayTmp = PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalNote(medModel, props.medicalSummaryModel, noteModel) as MedicalItemMedicationAndPrescription[];
        const medicationToDisplay = medicationToDisplayTmp.map(x => new MedicationModel(x));

        return isArrayNullOrUndefinedOrEmpty(medicationToDisplay) ? null : (
            <ActiveMedication
                activeMedications={medicationToDisplay}
                encounter={encounter}
            />
        );
    }

    const problemsRender: JSX.Element = (
        <ProblemsSummary
            noteModel={noteModel}
            medicalSummaryModel={props.medicalSummaryModel}
            dateOfBirth={props.dateOfBirth}
            isSignedOff={isSignedOff}
            isEditMode={props.isEditMode}
            onMedicalItemClick={(item, view) => props.onMedicalItemClick(item, view)}
            addMedicalHistoryItem={() => props.addMedicalHistoryItem()}
        />
    );

    const allMedicalItems = props.medicalSummaryModel.get("MedicalItemsScreeningTest").toArray();
    let medicalItemsByEMRTemplate = toLookupDictionary(allMedicalItems, x => x.EMRTemplateId);

    medicalItemsByEMRTemplate = medicalItemsByEMRTemplate.filterGroup(group => {
        const latestInGroup = group.sortByPropertyDesc(x => x.CreationDate).firstOrDefault();
        const isVisible = new MedicalItemScreeningTest(latestInGroup).getCPPVisibility();
        return isVisible ||
            latestInGroup.HideDate == null ||
            moment(latestInGroup.HideDate) > moment(encounter.ConsultationDate) ||
            group.some(x => x.RelatedEncounterId === encounter.Id);
    });

    const screeningTestToDisplay =
        (
            PatientMedicalSummaryHelper.prepareMedicalItemForDisplayInNote(
                medicalItemsByEMRTemplate,
                props.medicalSummaryModel.get("EMRTemplateValidationNotes"),
                encounter
            ) as MedicalItemScreeningTestResponse[]
        ).map(x => new MedicalItemScreeningTest(x));

    const noOtherItems = !summaryItems.any(item => !!item.content) && !screeningTestToDisplay?.any();

    let shouldDisplayItems: boolean = props.isSectionOpen;

    const isEmpty = !problemsRender && noOtherItems;

    let diagnosticClass = "patient-note";
    let otherItemsStyle = { paddingTop: "0px" };

    if (!isSignedOff || (isSignedOff && !problemsRender && !noOtherItems)) {
        diagnosticClass = "patient-note separator";
        otherItemsStyle = null;
    }

    if (isSignedOff && !props.isReadOnly)
        shouldDisplayItems = !shouldDisplayItems;

    return (isSignedOff && isEmpty) ? null : (
        <>
            <div className="note-block medical-note-block" data-cy="medicalNote-medicalHistory">
                <div className="note-block-header" onClick={() => props.toggleSectionOpen()}>
                    <div className="pull-right">
                        <span className={"glyphicon " + (shouldDisplayItems ? "glyphicon-chevron-up" : "glyphicon-chevron-down")} />
                    </div>
                    <h3>{getText("MedicalSummaryMLabel")}</h3>
                </div>

                {shouldDisplayItems && (
                    <div className={props.isReadOnly ? "no-hover" : ""}>
                        <div className={diagnosticClass}>
                            {problemsRender}
                        </div>
                        <ScreeningPlan
                            isEncounterSignedOff={isSignedOff}
                            isEditMode={props.isEditMode}
                            screeningTests={screeningTestToDisplay}
                            addItemCallback={() => addScreeningPlanItem()}
                            selectItemCallback={screeningTest => props.onMedicalItemClick(screeningTest.model, MedicalItemsTab.ScreeningTest)}
                        />
                        <div className="patient-note" style={otherItemsStyle}>
                            <table className="patient-note">
                                <tbody>
                                    {summaryItems.map((item, i) => <SummaryItems key={`${item.title}_${i}`} {...item} />)}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            <div className="padding-sm" />
        </>
    );
}

export { MedicalSummarySection };
